import type { ReactNode } from "react";
import {
  Box,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

const SKELETON_ROWS = 8;
// Approximate rendered heights (px), used to work out how many placeholder
// rows fill the previous table's height. Rows are sized like real rows
// (name + email stacked), not single text lines.
const SKELETON_ROW_HEIGHT = 53;
const SKELETON_CHROME_HEIGHT = 102; // header row + footer

const BAR_SX = { borderRadius: "4px", bgcolor: "grey.100" };

function Line({ width, height = 12 }: { width: number | string; height?: number }) {
  return <Skeleton variant="rounded" animation="wave" width={width} height={height} sx={BAR_SX} />;
}

function TwoLines({ top, bottom }: { top: number; bottom: number }) {
  return (
    <Stack spacing={0.75}>
      <Line width={top} height={14} />
      <Line width={bottom} height={10} />
    </Stack>
  );
}

function ScoreBar() {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Line width={64} height={5} />
      <Line width={26} height={16} />
    </Stack>
  );
}

function ChipShape({ width }: { width: number }) {
  return <Skeleton variant="rounded" animation="wave" width={width} height={24} sx={{ ...BAR_SX, borderRadius: "4px" }} />;
}

// One placeholder per real column (see LeadsTable.tsx's COLUMNS), shaped like
// that column's content. Widths vary slightly by row so it doesn't look like a grid.
// Widths mirror LeadsTable's fixed column widths; the first column holds
// the checkbox + ID together, like the real table.
const COLUMNS: Array<{ header: number; cell: (row: number) => ReactNode; width: number }> = [
  { header: 18, cell: () => <Line width={20} />, width: 96 },
  { header: 70, cell: (r) => <TwoLines top={[120, 150, 100, 135][r % 4]} bottom={[160, 180, 140, 170][r % 4]} />, width: 240 },
  { header: 60, cell: () => <ScoreBar />, width: 114 },
  { header: 72, cell: () => <ScoreBar />, width: 168 },
  { header: 70, cell: (r) => <Line width={[90, 110, 80, 100][r % 4]} />, width: 172 },
  { header: 80, cell: (r) => <ChipShape width={[80, 96, 72, 104][r % 4]} />, width: 144 },
  { header: 96, cell: (r) => <ChipShape width={[70, 88, 76, 70][r % 4]} />, width: 168 },
  { header: 44, cell: (r) => <Line width={[50, 30, 56, 40][r % 4]} />, width: 120 },
  { header: 60, cell: () => <TwoLines top={72} bottom={48} />, width: 100 },
  { header: 64, cell: (r) => <Line width={[88, 96, 80, 92][r % 4]} />, width: 114 },
  { header: 70, cell: () => <Line width={60} />, width: 176 },
];

const CHECKBOX_BOX = <Skeleton variant="rounded" animation="wave" width={18} height={18} sx={{ ...BAR_SX, flexShrink: 0 }} />;

// `height` is the last rendered table's height — when given, enough rows are
// drawn to fill it so swapping table ↔ skeleton doesn't change the page height.
export default function LeadsSkeleton({ height }: { height?: number | null }) {
  const rowCount = height
    ? Math.max(SKELETON_ROWS, Math.floor((height - SKELETON_CHROME_HEIGHT) / SKELETON_ROW_HEIGHT))
    : SKELETON_ROWS;

  return (
    <Paper elevation={0} sx={{ mx: -2, overflow: "hidden", border: "none", flex: 1, display: "flex", flexDirection: "column" }}>
      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto", flex: 1 }}>
        <Table
          size="small"
          sx={{
            minWidth: 1580,
            tableLayout: "fixed",
            "& .MuiTableCell-root": { px: 1, borderColor: "grey.200" },
            "& .MuiTableCell-root:first-of-type, & .MuiTableCell-root:nth-of-type(2)": { pl: 2 },
            "& .MuiTableCell-root:last-of-type": { pr: 2 },
            "& .MuiTableBody-root .MuiTableCell-root": { py: 1 },
          }}
        >
          <TableHead>
            <TableRow>
              {COLUMNS.map((column, index) => (
                <TableCell key={index} sx={{ width: column.width }}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    {index === 0 && CHECKBOX_BOX}
                    <Line width={column.header} height={14} />
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: rowCount }).map((_, rowIndex) => (
              <TableRow key={rowIndex} sx={{ height: SKELETON_ROW_HEIGHT }}>
                {COLUMNS.map((column, columnIndex) => (
                  <TableCell key={columnIndex}>
                    {columnIndex === 0 ? (
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        {CHECKBOX_BOX}
                        {column.cell(rowIndex)}
                      </Stack>
                    ) : (
                      column.cell(rowIndex)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Line width={160} height={14} />
          <Stack direction="row" spacing={1}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} variant="circular" animation="wave" width={28} height={28} sx={{ bgcolor: "grey.100" }} />
            ))}
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
}
